import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, EmptyState } from "@/components/Layout";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { dateTime, lots } from "@/lib/format";
import { useRefreshDirectory } from "@/lib/queries";
import type { Closure, DeletedRoots } from "@/lib/types";

/**
 * GLI ELIMINATI: conto, ciclo e fase non si cancellano, si timbrano — e da qui
 * tornano, se non violano un unico parziale (LOGIN_TAKEN, SEQ_TAKEN). Prima
 * stava in fondo alla pagina Clienti; una pagina sua perché non è più solo
 * clienti che finiscono qui, e la scorreva.
 */

type Kind = "accounts" | "cycles" | "sessions";

interface Row {
  id: string;
  kind: Kind;
  tipo: string;
  nome: string;
  cliente: string;
  deletedAt: string;
  chi: string | null;
  closures: Closure[] | null;
}

function rowsFrom(data: DeletedRoots): Row[] {
  return [
    ...data.accounts.map((a) => ({
      id: a.id,
      kind: "accounts" as const,
      tipo: "Conto",
      nome: a.label,
      cliente: a.client.name,
      deletedAt: a.deletedAt,
      chi: a.deletedBy,
      closures: a.closures,
    })),
    ...data.cycles.map((c) => ({
      id: c.id,
      kind: "cycles" as const,
      tipo: "Ciclo",
      nome: c.label ?? "—",
      cliente: c.client.name,
      deletedAt: c.deletedAt,
      chi: c.deletedBy,
      closures: c.closures,
    })),
    ...data.sessions.map((f) => ({
      id: f.id,
      kind: "sessions" as const,
      tipo: "Fase",
      nome: `Fase ${f.phase} · ${f.canonicalSymbol}`,
      cliente: f.cycle.client.name,
      deletedAt: f.deletedAt,
      chi: f.deletedBy,
      closures: f.closures,
    })),
  ];
}

/** Esito delle chiusure a mercato. Rosso se qualcosa è rimasto aperto; nullo = non registrato, non «nessuna». */
function EsitoChiusure({ closures }: { closures: Closure[] | null }) {
  if (closures === null) return <span className="text-xs text-faint">chiusure: non registrate</span>;
  if (!closures.length) return <span className="text-xs text-muted">nessuna posizione a mercato</span>;
  const rimaste = closures.filter((c) => c.esito === "RIMASTA_APERTA");
  if (!rimaste.length) return <span className="text-xs text-muted">{closures.length === 1 ? "1 posizione chiusa" : `${closures.length} posizioni chiuse`}</span>;
  return (
    <span className="text-xs text-loss">
      {rimaste.length === 1 ? "1 posizione rimasta aperta" : `${rimaste.length} posizioni rimaste aperte`}:{" "}
      {rimaste.map((c) => `${c.accountLabel} ${c.symbol} ${lots(c.lots)} lotti, ticket ${c.ticket}${c.motivo ? ` (${c.motivo})` : ""}`).join("; ")}
    </span>
  );
}

/** Il pulsante ripristina, col messaggio del backend in caso di rifiuto (LOGIN_TAKEN, SEQ_TAKEN). */
function Ripristina({ row }: { row: Row }) {
  const refresh = useRefreshDirectory();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.undelete(row.kind, row.id);
      await Promise.all([qc.invalidateQueries({ queryKey: ["deleted"] }), refresh()]);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void go()}>
        {busy ? "Ripristino…" : "Ripristina"}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}

const KIND_LABEL: Record<"TUTTI" | Kind, string> = {
  TUTTI: "Tutti",
  accounts: "Conto",
  cycles: "Ciclo",
  sessions: "Fase",
};

export function Deleted() {
  const canWrite = useCanWrite();
  const q = useQuery({ queryKey: ["deleted"], queryFn: () => api.deleted() });
  const [kind, setKind] = useState<"TUTTI" | Kind>("TUTTI");

  const header = <PageHeader title="Eliminati" subtitle="Conti, cicli e fasi eliminati: da qui tornano, se non violano un vincolo" />;

  if (q.isLoading) {
    return (
      <>
        {header}
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      </>
    );
  }
  if (q.error) {
    return (
      <>
        {header}
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        </div>
      </>
    );
  }

  const rows = q.data ? rowsFrom(q.data) : [];
  const counts: Record<"TUTTI" | Kind, number> = { TUTTI: rows.length, accounts: 0, cycles: 0, sessions: 0 };
  for (const r of rows) counts[r.kind]++;
  const filtered = kind === "TUTTI" ? rows : rows.filter((r) => r.kind === kind);

  const pill = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
      active ? "border-accent text-ink" : "border-line text-muted hover:text-ink"
    }`;

  const columns: DataTableColumn<Row>[] = [
    { key: "tipo", header: "Tipo", cell: (r) => r.tipo, sortBy: (r) => r.tipo },
    { key: "nome", header: "Nome", cell: (r) => r.nome, sortBy: (r) => r.nome },
    { key: "cliente", header: "Cliente", cell: (r) => r.cliente, sortBy: (r) => r.cliente },
    { key: "deletedAt", header: "Eliminato il", cell: (r) => dateTime(r.deletedAt), sortBy: (r) => r.deletedAt },
    { key: "chi", header: "Da chi", cell: (r) => r.chi ?? "utente non più leggibile" },
    { key: "esito", header: "Esito chiusure", cell: (r) => <EsitoChiusure closures={r.closures} /> },
    ...(canWrite ? [{ key: "azioni", header: "", align: "right" as const, cell: (r: Row) => <Ripristina row={r} /> }] : []),
  ];

  return (
    <>
      {header}
      <div className="space-y-4 px-6 py-5">
        <div className="flex flex-wrap items-center gap-2">
          {(["TUTTI", "accounts", "cycles", "sessions"] as const).map((k) => (
            <button key={k} onClick={() => setKind(k)} className={pill(kind === k)}>
              {KIND_LABEL[k]} <span className="text-faint">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="card">
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            initialSort={{ key: "deletedAt", dir: "desc" }}
            empty={{ title: "Niente di eliminato", hint: "Conti, cicli e fasi eliminati compaiono qui, con la possibilità di tornare indietro." }}
          />
        </div>
      </div>
    </>
  );
}
